import { useEffect, useMemo, useState } from 'react'
import {
  AlertTriangle,
  ArrowLeft,
  CheckCircle2,
  Clock,
  Filter,
  Navigation,
  RefreshCw,
  X,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import type { LatLngExpression, default as LeafletDefault } from 'leaflet'
import type {
  MapContainer as MapContainerType,
  Marker as MarkerType,
  Polygon as PolygonType,
  TileLayer as TileLayerType,
  useMapEvents as useMapEventsType,
  useMap as useMapType,
} from 'react-leaflet'
import type { default as MarkerClusterGroupType } from 'react-leaflet-cluster'
import type { Vehicle } from '@/lib/api'
import type { UIAccount } from '@/lib/use-accounts'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Badge } from '@/components/ui/badge'
import { useAccounts } from '@/lib/use-accounts'
import {
  useAccessories,
  useBookCar,
  useCancelRental,
  useVehicle,
  useVehicleModels,
  useVehicleStream,
  useVehicles,
} from '@/lib/api'
import { useAuth } from '@/lib/auth-oidc'
import { useTheme } from '@/lib/theme'
import { formatTimeAgo, getApiErrorMessage, localizeDigits } from '@/lib/utils'
import { ServiceZoneLayer } from '@/components/service-zone-layer'
import { RentalBanner } from '@/components/rental-banner'
import { CarDetailsOverlay } from '@/components/car-details-overlay'
import { useFeatureFlag } from '@/lib/use-feature-flags'

// Leaflet imports - only used in client component
let L: typeof LeafletDefault | undefined
let MapContainer: typeof MapContainerType | undefined
let TileLayer: typeof TileLayerType | undefined
let Marker: typeof MarkerType | undefined
let Polygon: typeof PolygonType | undefined
let useMap: typeof useMapType | undefined
let useMapEvents: typeof useMapEventsType | undefined
let MarkerClusterGroup: typeof MarkerClusterGroupType | undefined

// Initialize Leaflet only on client
const initLeaflet = async () => {
  if (typeof window === 'undefined') return

  const leafletModule = await import('leaflet')
  const reactLeafletModule = await import('react-leaflet')
  await import('leaflet/dist/leaflet.css')
  await import('leaflet.markercluster/dist/MarkerCluster.css')
  await import('leaflet.markercluster/dist/MarkerCluster.Default.css')
  const clusterModule = await import('react-leaflet-cluster')

  L = leafletModule.default
  MarkerClusterGroup = clusterModule.default
  MapContainer = reactLeafletModule.MapContainer
  TileLayer = reactLeafletModule.TileLayer
  Marker = reactLeafletModule.Marker
  Polygon = reactLeafletModule.Polygon
  useMap = reactLeafletModule.useMap
  useMapEvents = reactLeafletModule.useMapEvents

  // Fix for default marker icons in React-Leaflet
  const iconRetinaUrl =
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png'
  const iconUrl =
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png'
  const shadowUrl =
    'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png'

  const DefaultIcon = L.icon({
    iconRetinaUrl,
    iconUrl,
    shadowUrl,
    iconSize: [25, 41],
    iconAnchor: [12, 41],
    popupAnchor: [1, -34],
    tooltipAnchor: [16, -28],
    shadowSize: [41, 41],
  })

  L.Marker.prototype.options.icon = DefaultIcon
}

// Car icon - create a custom colored marker
const createCarIcon = (color: string = '#3b82f6') => {
  if (!L) return undefined
  return L.divIcon({
    html: `
      <div style="
        width: 24px;
        height: 24px;
        background-color: ${color};
        border: 3px solid white;
        border-radius: 50% 50% 50% 0;
        transform: rotate(-45deg);
        box-shadow: 0 2px 8px rgba(0,0,0,0.3);
        position: relative;
      ">
        <div style="
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%) rotate(45deg);
          width: 8px;
          height: 8px;
          background-color: white;
          border-radius: 50%;
        "></div>
      </div>
    `,
    className: 'car-marker',
    iconSize: [24, 24],
    iconAnchor: [12, 24],
    popupAnchor: [0, -24],
  })
}

// User location icon - create a blue pulsing marker
const createUserLocationIcon = () => {
  if (!L) return undefined
  return L.divIcon({
    html: `
      <div style="position: relative; width: 20px; height: 20px;">
        <div style="
          position: absolute;
          width: 20px;
          height: 20px;
          background-color: #3b82f6;
          border: 3px solid white;
          border-radius: 50%;
          box-shadow: 0 2px 8px rgba(0,0,0,0.3);
          z-index: 2;
        "></div>
        <div style="
          position: absolute;
          width: 20px;
          height: 20px;
          background-color: rgba(59, 130, 246, 0.3);
          border-radius: 50%;
          animation: pulse 2s ease-out infinite;
        "></div>
      </div>
      <style>
        @keyframes pulse {
          0% {
            transform: scale(1);
            opacity: 1;
          }
          100% {
            transform: scale(2.5);
            opacity: 0;
          }
        }
      </style>
    `,
    className: 'user-location-marker',
    iconSize: [20, 20],
    iconAnchor: [10, 10],
  })
}

// Montreal coordinates
const MONTREAL_CENTER: [number, number] = [45.5017, -73.5673]

// Car data type for map display
export interface Car {
  id: string
  name: string
  licensePlate: string
  location: LatLngExpression
  status: 'available' | 'reserved' | 'in-use'
  fuelLevel?: number
  model?: string
  isAWD?: boolean
  vehiclePropulsionTypeId?: number
  vehicleAccessories?: Array<number>
  lastSeen?: string
  displayZone?: boolean
}

// Communauto account data type - now using UIAccount from use-accounts
export type CommunautoAccount = UIAccount

// Transform API Vehicle to Car interface
function transformVehicleToCar(vehicle: Vehicle): Car {
  return {
    id: vehicle.vehicleId.toString(),
    name:
      vehicle.model || `Vehicle #${localizeDigits(String(vehicle.vehicleNb))}`,
    licensePlate: `#${localizeDigits(String(vehicle.vehicleNb))}`,
    location: [
      vehicle.vehicleLocation.latitude,
      vehicle.vehicleLocation.longitude,
    ],
    status: 'available', // Vehicles from API are available
    fuelLevel: vehicle.energyLevelPercentage ?? undefined,
    model: vehicle.model ?? undefined,
    isAWD: vehicle.isAWD,
    vehiclePropulsionTypeId: vehicle.vehiclePropulsionTypeId,
    vehicleAccessories: vehicle.vehicleAccessories,
    lastSeen: vehicle.lastSeen ?? undefined,
    displayZone: vehicle.displayZone,
  }
}

// Component to handle map centering - must be defined outside to avoid hook order issues
// Only rendered when Leaflet is loaded and useMap is available
function MapCenter({ center }: { center: LatLngExpression }) {
  // useMap will be available when this component renders (it's only rendered inside MapContainer)
  const map = useMap!()
  useEffect(() => {
    map.setView(center, map.getZoom())
  }, [center, map])
  return null
}

// Component to handle map clicks for closing popup
function MapClickHandler({ onMapClick }: { onMapClick: () => void }) {
  useMapEvents!({
    click: () => {
      onMapClick()
    },
  })
  return null
}

export function CarsMap() {
  const [isClient, setIsClient] = useState(false)
  const [leafletLoaded, setLeafletLoaded] = useState(false)
  const { t } = useTranslation()
  const { isAuthenticated } = useAuth()
  const { resolvedTheme } = useTheme()
  const { accounts: accountsList, isLoading: accountsLoading } = useAccounts()
  const [userLocation, setUserLocation] = useState<LatLngExpression | null>(
    null,
  )
  const [mapCenter, setMapCenter] = useState<LatLngExpression>(MONTREAL_CENTER)
  const [selectedCar, setSelectedCar] = useState<Car | null>(null)
  const [showAccountSelection, setShowAccountSelection] = useState(false)
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(
    null,
  )
  const [countdown, setCountdown] = useState(15)
  const [showVehicleDetails, setShowVehicleDetails] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [selectedModels, setSelectedModels] = useState<Set<string>>(new Set())
  const [selectedAccessoryIds, setSelectedAccessoryIds] = useState<Set<number>>(
    new Set(),
  )
  const [rentalDashboardEnabled] = useFeatureFlag('rentalDashboard')
  const [liveMode, setLiveMode] = useState(() => {
    if (typeof window === 'undefined') return false
    return localStorage.getItem('vehicleLiveMode') === 'true'
  })

  // WebSocket vehicle stream (active when liveMode is on)
  useVehicleStream({ enabled: liveMode && isAuthenticated, active: true })

  // Use TanStack Query for accessories and vehicles
  const {
    data: vehiclesData,
    isLoading: vehiclesLoading,
    refetch: refetchVehicles,
    dataUpdatedAt,
  } = useVehicles({
    enabled: isAuthenticated,
    refetchInterval: liveMode ? false : 15 * 1000,
  })

  // Transform vehicles to Car interface and apply filters
  const cars = useMemo(() => {
    if (!vehiclesData?.vehicles) return []
    return vehiclesData.vehicles
      .filter((vehicle) => {
        // Filter by model
        if (
          selectedModels.size > 0 &&
          (!vehicle.model || !selectedModels.has(vehicle.model))
        ) {
          return false
        }
        // Filter by accessories
        if (selectedAccessoryIds.size > 0) {
          const vehicleAcc = new Set(vehicle.vehicleAccessories ?? [])
          for (const reqId of selectedAccessoryIds) {
            if (!vehicleAcc.has(reqId)) return false
          }
        }
        return true
      })
      .map(transformVehicleToCar)
  }, [vehiclesData, selectedModels, selectedAccessoryIds])

  const activeFilterCount = selectedModels.size + selectedAccessoryIds.size

  // Fetch accessories list and vehicle models
  const { data: accessoriesData } = useAccessories()
  const { data: vehicleModelsData } = useVehicleModels()

  // Fetch vehicle details when requested
  const selectedVehicleId = selectedCar
    ? parseInt(selectedCar.id, 10)
    : undefined
  const { data: vehicleDetails, isLoading: vehicleDetailsLoading } = useVehicle(
    showVehicleDetails ? selectedVehicleId : undefined,
  )

  // Mutations
  const bookCarMutation = useBookCar()
  const cancelRentalMutation = useCancelRental()

  // Combined loading state
  const isLoading =
    vehiclesLoading ||
    bookCarMutation.isPending ||
    cancelRentalMutation.isPending

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsClient(true)
      initLeaflet().then(() => {
        setLeafletLoaded(true)
      })
    }
  }, [])

  // Get user location on mount - must be called before any early returns
  useEffect(() => {
    if (!isClient || typeof window === 'undefined') return // Don't run on server
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords
        setUserLocation([latitude, longitude])
        setMapCenter([latitude, longitude])
      },
      (error) => {
        console.error('Error getting user location:', error)
        // Default to Montreal if geolocation fails
        // Don't show alert on initial load, only when user clicks the button
        setMapCenter(MONTREAL_CENTER)
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 60000, // Accept cached position up to 1 minute old
      },
    )
  }, [isClient])

  // Auto-select first account if none selected
  useEffect(() => {
    if (!selectedAccountId && accountsList.length > 0) {
      setSelectedAccountId(accountsList[0].id)
    }
  }, [selectedAccountId, accountsList])

  // Countdown timer - resets when data is refetched
  useEffect(() => {
    // Reset countdown when data is updated
    setCountdown(15)
  }, [dataUpdatedAt])

  // Update countdown every second
  useEffect(() => {
    if (!isClient) return

    const interval = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          return 15 // Reset to 15 when it reaches 0
        }
        return prev - 1
      })
    }, 1000)

    return () => clearInterval(interval)
  }, [isClient])

  // Reset showVehicleDetails when selected car changes
  useEffect(() => {
    setShowVehicleDetails(false)
  }, [selectedCar?.id])

  // Tile layer configuration based on theme
  const tileLayerConfig = useMemo(() => {
    if (resolvedTheme === 'dark') {
      return {
        url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
      }
    }
    return {
      url: 'https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png',
      attribution:
        '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
    }
  }, [resolvedTheme])

  // Don't render during SSR - must be after all hooks
  if (!isClient || !leafletLoaded || !MapContainer || !TileLayer || !Marker) {
    return (
      <div className="relative h-full w-full flex items-center justify-center">
        <div className="text-muted-foreground">{t('carsMap.loadingMap')}</div>
      </div>
    )
  }

  const handleRefresh = async () => {
    if (!isAuthenticated || !accountsList.length) {
      toast.warning(
        t('carsMap.pleaseSelectAccount') || 'Please select an account',
      )
      return
    }
    setCountdown(15) // Reset countdown on manual refresh
    await refetchVehicles()
  }

  const handleGoToUserLocation = () => {
    if (userLocation) {
      setMapCenter(userLocation)
      return
    }

    // Request location with options
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const { latitude, longitude } = position.coords
        const location: LatLngExpression = [latitude, longitude]
        setUserLocation(location)
        setMapCenter(location)
      },
      (error) => {
        console.error('Error getting user location:', error)
        let message = t('carsMap.unableToGetLocation')

        switch (error.code) {
          case error.PERMISSION_DENIED:
            message = t('carsMap.locationPermissionDenied')
            break
          case error.POSITION_UNAVAILABLE:
            message = t('carsMap.locationUnavailable')
            break
          case error.TIMEOUT:
            message = t('carsMap.locationTimeout')
            break
          default:
            message = t('carsMap.locationError')
            break
        }

        toast.error(message)
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0,
      },
    )
  }

  const handleReserveClick = () => {
    setShowAccountSelection(true)
    // Auto-select first account if none selected
    if (!selectedAccountId && accountsList.length > 0) {
      setSelectedAccountId(accountsList[0].id)
    }
  }

  const handleConfirmReservation = async () => {
    if (!selectedAccountId || !selectedCar) return

    try {
      // Book the car using vehicle ID
      const vehicleId = parseInt(selectedCar.id, 10)
      await bookCarMutation.mutateAsync({
        accountName: selectedAccountId,
        vehicleId,
      })

      // Reset state
      setShowAccountSelection(false)
      setSelectedCar(null)

      // Show success message
      toast.success(
        t('carsMap.reservationConfirmed') || 'Reservation confirmed!',
      )
    } catch (error) {
      console.error('Failed to reserve car:', error)
      toast.error(
        getApiErrorMessage(
          error,
          t('carsMap.reservationFailed') ||
            'Failed to reserve car. Please try again.',
        ),
      )
    }
  }

  const formatTimeRemaining = (date: Date): string => {
    const now = new Date()
    const diffMs = date.getTime() - now.getTime()
    const diffHours = Math.floor(diffMs / 3600000)
    const diffMins = Math.floor((diffMs % 3600000) / 60000)

    if (diffHours > 0) {
      return localizeDigits(
        `${diffHours}h ${diffMins}m ${t('common.remaining')}`,
      )
    } else {
      return localizeDigits(`${diffMins}m ${t('common.remaining')}`)
    }
  }

  // Helper to get accessory names from IDs
  const getAccessoryNames = (
    accessoryIds: Array<number> | undefined,
  ): Array<string> => {
    if (!accessoryIds || !accessoriesData) return []
    return accessoryIds
      .map((id) => {
        const accessory = accessoriesData.find((acc) => acc.accessoryId === id)
        return accessory?.accessoryName
      })
      .filter((name): name is string => !!name)
  }

  // Helper to determine if vehicle is electric
  const isElectric = (propulsionTypeId: number | undefined): boolean => {
    return propulsionTypeId === 2 // ID 2 is electric according to schema
  }

  // At this point, we know Leaflet is loaded, so MapContainer, TileLayer, Marker, and useMap are all available
  const MapContainerComponent = MapContainer
  const TileLayerComponent = TileLayer
  const MarkerComponent = Marker

  return (
    <div className="relative h-full w-full">
      <MapContainerComponent
        center={mapCenter}
        zoom={15}
        style={{ height: '100%', width: '100%', zIndex: 0 }}
        scrollWheelZoom={true}
      >
        <TileLayerComponent
          key={resolvedTheme}
          attribution={tileLayerConfig.attribution}
          url={tileLayerConfig.url}
        />
        <MapCenter center={mapCenter} />
        <MapClickHandler
          onMapClick={() => {
            if (selectedCar && !showAccountSelection) {
              setSelectedCar(null)
              setShowVehicleDetails(false)
            }
          }}
        />
        {/* User location marker */}
        {userLocation && (
          <MarkerComponent
            position={userLocation}
            icon={createUserLocationIcon()}
            zIndexOffset={1000}
          />
        )}
        {/* Service zone boundaries */}
        <ServiceZoneLayer />
        {/* Car markers with clustering */}
        {MarkerClusterGroup ? (
          <MarkerClusterGroup
            chunkedLoading
            maxClusterRadius={20}
            disableClusteringAtZoom={15}
            spiderfyOnMaxZoom
            showCoverageOnHover={false}
          >
            {cars.map((car) => {
              const isSelected = selectedCar?.id === car.id
              const iconColor = isSelected ? '#10b981' : '#3b82f6'
              return (
                <MarkerComponent
                  key={car.id}
                  position={car.location}
                  icon={createCarIcon(iconColor)}
                  eventHandlers={{
                    click: () => {
                      setSelectedCar(car)
                    },
                  }}
                />
              )
            })}
          </MarkerClusterGroup>
        ) : (
          cars.map((car) => {
            const isSelected = selectedCar?.id === car.id
            const iconColor = isSelected ? '#10b981' : '#3b82f6'
            return (
              <MarkerComponent
                key={car.id}
                position={car.location}
                icon={createCarIcon(iconColor)}
                eventHandlers={{
                  click: () => {
                    setSelectedCar(car)
                  },
                }}
              />
            )
          })
        )}
      </MapContainerComponent>

      {/* Active Rental Banner */}
      <RentalBanner userLocation={userLocation} />

      {/* Control Buttons */}
      <div className="absolute bottom-4 right-4 z-[1000] flex flex-col gap-2">
        <Button
          onClick={() => setShowFilters(true)}
          className="h-12 w-12 rounded-full shadow-lg bg-background hover:bg-accent relative"
          variant="outline"
        >
          <Filter className="h-5 w-5" />
          {activeFilterCount > 0 && (
            <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary text-primary-foreground text-xs flex items-center justify-center">
              {localizeDigits(String(activeFilterCount))}
            </span>
          )}
        </Button>
        <Button
          onClick={handleGoToUserLocation}
          size="icon"
          className="h-12 w-12 rounded-full shadow-lg bg-background hover:bg-accent"
          variant="outline"
        >
          <Navigation className="h-5 w-5" />
        </Button>
        {/* Live mode toggle */}
        <Button
          onClick={() => {
            const next = !liveMode
            setLiveMode(next)
            localStorage.setItem('vehicleLiveMode', String(next))
          }}
          className={`h-12 w-12 rounded-full shadow-lg ${
            liveMode
              ? 'bg-green-500 hover:bg-green-600 text-white border-green-500'
              : 'bg-background hover:bg-accent'
          }`}
          variant={liveMode ? 'default' : 'outline'}
        >
          <div className="flex flex-col items-center justify-center">
            <span
              className={`text-[10px] font-bold leading-none ${liveMode ? 'animate-pulse' : ''}`}
            >
              {t('cache.live.badge')}
            </span>
          </div>
        </Button>
        {/* Refresh / countdown button (only in polling mode) */}
        {!liveMode && (
          <Button
            onClick={handleRefresh}
            className="h-12 w-12 rounded-full shadow-lg bg-background hover:bg-accent"
            variant="outline"
            disabled={isLoading}
          >
            {isLoading ? (
              <RefreshCw className="h-5 w-5 animate-spin" />
            ) : (
              <div className="flex flex-col items-center justify-center">
                <span className="text-xs font-bold">
                  {localizeDigits(String(countdown))}
                </span>
                <span className="text-[8px] leading-none">
                  {t('carsMap.sec')}
                </span>
              </div>
            )}
          </Button>
        )}
      </div>

      {/* Filter Panel */}
      {showFilters && (
        <div className="absolute inset-0 z-[1001] bg-black/50 backdrop-blur-sm">
          <div className="absolute inset-x-0 bottom-0 z-[1002] max-h-[80vh] flex flex-col rounded-t-2xl bg-background shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-border bg-background p-4 shrink-0">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowFilters(false)}
                >
                  <X className="h-5 w-5" />
                </Button>
                <div>
                  <CardTitle>{t('carsMap.filters')}</CardTitle>
                  <CardDescription className="text-xs">
                    {localizeDigits(String(cars.length))}{' '}
                    {t('carsMap.vehiclesShown')}
                  </CardDescription>
                </div>
              </div>
              {activeFilterCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSelectedModels(new Set())
                    setSelectedAccessoryIds(new Set())
                  }}
                >
                  {t('search.reset')}
                </Button>
              )}
            </div>

            {/* Scrollable Filter Content */}
            <div className="flex-1 overflow-y-auto p-4 space-y-6">
              {/* Model Filter */}
              <div>
                <h3 className="text-sm font-medium mb-3">
                  {t('carsMap.filterByModel')}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {(vehicleModelsData ?? []).map((model) => {
                    const isActive = selectedModels.has(model)
                    return (
                      <Badge
                        key={model}
                        variant={isActive ? 'default' : 'outline'}
                        className="cursor-pointer text-xs py-1.5 px-3"
                        onClick={() => {
                          setSelectedModels((prev) => {
                            const next = new Set(prev)
                            if (next.has(model)) {
                              next.delete(model)
                            } else {
                              next.add(model)
                            }
                            return next
                          })
                        }}
                      >
                        {model}
                      </Badge>
                    )
                  })}
                </div>
              </div>

              {/* Accessory Filter */}
              <div>
                <h3 className="text-sm font-medium mb-3">
                  {t('carsMap.filterByAccessory')}
                </h3>
                <div className="flex flex-wrap gap-2">
                  {(accessoriesData ?? []).map((acc) => {
                    const isActive = selectedAccessoryIds.has(acc.accessoryId)
                    return (
                      <Badge
                        key={acc.accessoryId}
                        variant={isActive ? 'default' : 'outline'}
                        className="cursor-pointer text-xs py-1.5 px-3"
                        onClick={() => {
                          setSelectedAccessoryIds((prev) => {
                            const next = new Set(prev)
                            if (next.has(acc.accessoryId)) {
                              next.delete(acc.accessoryId)
                            } else {
                              next.add(acc.accessoryId)
                            }
                            return next
                          })
                        }}
                      >
                        {acc.accessoryName}
                      </Badge>
                    )
                  })}
                </div>
              </div>
            </div>

            {/* Apply Button */}
            <div className="border-t border-border bg-background p-4 pb-safe-area-inset-bottom shrink-0">
              <Button className="w-full" onClick={() => setShowFilters(false)}>
                {t('carsMap.applyFilters')} (
                {localizeDigits(String(cars.length))}{' '}
                {t('carsMap.vehiclesShown')})
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Account Selection Panel */}
      {showAccountSelection && selectedCar && (
        <div className="absolute inset-0 z-[1001] bg-black/50 backdrop-blur-sm">
          <div className="absolute inset-x-0 bottom-0 z-[1002] max-h-[80vh] flex flex-col rounded-t-2xl bg-background shadow-2xl">
            {/* Header - Fixed */}
            <div className="flex items-center justify-between border-b border-border bg-background p-4 shrink-0">
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => setShowAccountSelection(false)}
                >
                  <ArrowLeft className="h-5 w-5" />
                </Button>
                <div>
                  <CardTitle>{t('carsMap.selectAccount')}</CardTitle>
                  <CardDescription className="text-xs">
                    {t('carsMap.reserveCar', {
                      name: selectedCar.name,
                      plate: selectedCar.licensePlate,
                    })}
                  </CardDescription>
                </div>
              </div>
            </div>

            {/* Scrollable Accounts List */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3">
              {accountsList.length === 0 && !accountsLoading && (
                <div className="text-center text-muted-foreground py-8">
                  {t('carsMap.noAccountsAvailable')}
                </div>
              )}
              {accountsLoading && (
                <div className="text-center text-muted-foreground py-8">
                  {t('common.loading')}...
                </div>
              )}
              {accountsList.map((account) => (
                <Card
                  key={account.id}
                  className={`cursor-pointer transition-all ${
                    selectedAccountId === account.id
                      ? 'ring-2 ring-primary'
                      : 'hover:bg-accent'
                  }`}
                  onClick={() => setSelectedAccountId(account.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <CardTitle className="text-base">
                            {account.name}
                          </CardTitle>
                          {selectedAccountId === account.id && (
                            <CheckCircle2 className="h-5 w-5 text-primary" />
                          )}
                        </div>
                        <p className="text-xs text-muted-foreground mb-2">
                          {account.email}
                        </p>
                        <div className="space-y-1 text-xs">
                          {account.hasActiveReservation ? (
                            <div className="flex items-center gap-2">
                              <Badge variant="destructive" className="text-xs">
                                {t('carsMap.activeReservation')}
                              </Badge>
                              <span className="text-muted-foreground">
                                {account.activeReservationCar}
                              </span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 text-muted-foreground">
                              <Clock className="h-3 w-3" />
                              <span>
                                {t('carsMap.lastTrip')}:{' '}
                                {account.lastTripEnd &&
                                  formatTimeAgo(account.lastTripEnd, t)}
                              </span>
                            </div>
                          )}
                          {account.nextFree ? (
                            account.nextFree.hasSubscription ? (
                              account.nextFree.isFree ? (
                                <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                                  <CheckCircle2 className="h-3 w-3" />
                                  <span>{t('carsMap.freeNow')}</span>
                                </div>
                              ) : account.nextFree.nextFreeTime ? (
                                <div className="flex items-center gap-2 text-amber-600 dark:text-amber-400">
                                  <Clock className="h-3 w-3" />
                                  <span>
                                    {t('carsMap.freeIn', {
                                      time: formatTimeRemaining(
                                        account.nextFree.nextFreeTime,
                                      ),
                                    })}
                                  </span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-2 text-muted-foreground">
                                  <Clock className="h-3 w-3" />
                                  <span className="text-xs">
                                    {t('carsMap.freeNotSoon')}
                                  </span>
                                </div>
                              )
                            ) : (
                              <div className="flex items-center gap-2 text-muted-foreground">
                                <span className="text-xs">
                                  {t('carsMap.freeNever')}
                                </span>
                              </div>
                            )
                          ) : null}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Fixed Bottom Section */}
            <div className="border-t border-border bg-background p-4 pb-safe-area-inset-bottom shrink-0 space-y-3">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setShowAccountSelection(false)
                    setSelectedAccountId(null)
                  }}
                >
                  {t('carsMap.cancel')}
                </Button>
                <Button
                  className="flex-1"
                  onClick={handleConfirmReservation}
                  disabled={!selectedAccountId || isLoading}
                >
                  {t('carsMap.confirmReservation')}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Car Details Popup (for mobile) */}
      {selectedCar &&
        !showAccountSelection &&
        (rentalDashboardEnabled ? (
          <CarDetailsOverlay
            car={selectedCar}
            userLocation={userLocation}
            accountName={selectedAccountId}
            onReserve={handleReserveClick}
            onClose={() => {
              setSelectedCar(null)
              setShowVehicleDetails(false)
            }}
          />
        ) : (
          <div className="absolute inset-x-0 bottom-0 z-[1000] p-4 pb-safe-area-inset-bottom">
            <Card>
              <CardHeader>
                <div className="flex items-center gap-2">
                  <CardTitle>{selectedCar.name}</CardTitle>
                  {selectedCar.isAWD && (
                    <Badge variant="secondary" className="text-xs">
                      AWD
                    </Badge>
                  )}
                </div>
                <CardDescription>{selectedCar.licensePlate}</CardDescription>
              </CardHeader>
              <CardContent>
                {selectedCar.displayZone && (
                  <div className="mb-3 flex items-start gap-2 rounded-md border border-orange-200 bg-orange-50 p-3 text-sm dark:border-orange-800 dark:bg-orange-950">
                    <AlertTriangle className="h-4 w-4 text-orange-600 dark:text-orange-400 shrink-0 mt-0.5" />
                    <span className="text-orange-800 dark:text-orange-200">
                      {t('carsMap.zoneWarning')}
                    </span>
                  </div>
                )}
                <div className="space-y-2 text-sm">
                  {selectedCar.model && (
                    <div>
                      <span className="font-medium">{t('carsMap.model')}:</span>{' '}
                      {selectedCar.model}
                    </div>
                  )}
                  {selectedCar.fuelLevel !== undefined && (
                    <div>
                      <span className="font-medium">
                        {isElectric(selectedCar.vehiclePropulsionTypeId)
                          ? t('carsMap.batteryLevel')
                          : t('carsMap.fuelLevel')}
                        :
                      </span>{' '}
                      {localizeDigits(`${selectedCar.fuelLevel}%`)}
                    </div>
                  )}
                  {selectedCar.lastSeen && (
                    <div>
                      <span className="font-medium">
                        {t('carsMap.lastSeen')}:
                      </span>{' '}
                      {formatTimeAgo(new Date(selectedCar.lastSeen), t)}
                    </div>
                  )}
                  {selectedCar.vehicleAccessories &&
                    selectedCar.vehicleAccessories.length > 0 && (
                      <div>
                        <span className="font-medium">
                          {t('carsMap.accessories')}:
                        </span>
                        <div className="flex flex-wrap gap-1 mt-1">
                          {getAccessoryNames(
                            selectedCar.vehicleAccessories,
                          ).map((name) => (
                            <Badge
                              key={name}
                              variant="outline"
                              className="text-xs"
                            >
                              {name}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  <div>
                    <span className="font-medium">{t('carsMap.status')}:</span>{' '}
                    <span className="capitalize">{selectedCar.status}</span>
                  </div>

                  {/* Show detailed info when loaded */}
                  {showVehicleDetails && vehicleDetails && (
                    <div className="border-t pt-2 mt-2 space-y-1">
                      <div className="text-xs text-muted-foreground mb-1.5">
                        {t('carsMap.additionalDetails')}
                      </div>
                      <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                        {vehicleDetails.vehiclePlate && (
                          <>
                            <span className="text-muted-foreground">
                              {t('carsMap.licensePlate')}:
                            </span>
                            <span className="font-medium">
                              {vehicleDetails.vehiclePlate}
                            </span>
                          </>
                        )}
                        {vehicleDetails.lastUsed && (
                          <>
                            <span className="text-muted-foreground">
                              {t('carsMap.lastUsed')}:
                            </span>
                            <span className="font-medium">
                              {formatTimeAgo(
                                new Date(vehicleDetails.lastUsed),
                                t,
                              )}
                            </span>
                          </>
                        )}
                        {vehicleDetails.colorLocalizedName && (
                          <>
                            <span className="text-muted-foreground">
                              {t('carsMap.color')}:
                            </span>
                            <span className="font-medium">
                              {vehicleDetails.colorLocalizedName}
                            </span>
                          </>
                        )}
                        {vehicleDetails.nbPlaces && (
                          <>
                            <span className="text-muted-foreground">
                              {t('carsMap.seats')}:
                            </span>
                            <span className="font-medium">
                              {localizeDigits(String(vehicleDetails.nbPlaces))}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <div className="mt-4 flex gap-2">
                  {!showVehicleDetails && (
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => setShowVehicleDetails(true)}
                      disabled={vehicleDetailsLoading}
                    >
                      {vehicleDetailsLoading
                        ? t('common.loading')
                        : t('carsMap.showMoreDetails')}
                    </Button>
                  )}
                  <Button
                    className="flex-1"
                    size="sm"
                    onClick={handleReserveClick}
                  >
                    {t('carsMap.reserve')}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        ))}
    </div>
  )
}
